import { useMemo, useState } from "react"
import { Link } from "react-router"
import { DndContext, type DragEndEvent, MouseSensor, TouchSensor, closestCorners, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core"
import { Phone01, SearchMd } from "@untitledui/icons"
import { toast } from "sonner"

import type { GhlOpportunityRow, GhlStage } from "@/lib/api/ghl-types"
import { useDecide, useGhlOpportunities, useGhlPipelines, useGhlUsers, usePatchOpportunity } from "@/lib/api/use-ghl"
import { TextSelect } from "@/components/ui/text-select"
import { cx } from "@/lib/utils/cx"
import { BookDialog } from "./book-dialog"
import { Empty, INPUT, pill, relTime, usd, usdK, userName } from "./shared"

// Pipeline — the location's stages as a board or a list. Drag to move;
// text, call or book from the card; won or lost with undo.

export function Pipeline({ teamId }: { teamId: number | string }) {
  const [owner, setOwner] = useState("")
  const [q, setQ] = useState("")
  const [view, setView] = useState<"board" | "list">("board")
  const [booking, setBooking] = useState<GhlOpportunityRow | null>(null)
  const { data: pipelines = [] } = useGhlPipelines(teamId)
  const { data: all = [], isLoading } = useGhlOpportunities(teamId, { status: "open", assignedTo: owner || undefined })
  const { data: users = [] } = useGhlUsers(teamId)
  const patch = usePatchOpportunity(teamId)
  const decide = useDecide(teamId)
  const stages = [...(pipelines[0]?.stages ?? [])].sort((a, b) => a.position - b.position)
  const opps = useMemo(() => { const n = q.trim().toLowerCase(); return n ? all.filter((o) => `${o.contactName} ${o.vehicle ?? ""} ${o.name}`.toLowerCase().includes(n)) : all }, [all, q])
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 6 } }), useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }))

  const onDragEnd = (e: DragEndEvent) => {
    const id = String(e.active.id); const to = e.over ? String(e.over.id) : null
    const o = opps.find((x) => x.id === id)
    if (!o || !to || to === o.pipelineStageId) return
    patch.mutate({ id, pipelineStageId: to }, { onError: () => toast.error("Couldn't move it — try again") })
  }
  const close = (o: GhlOpportunityRow, status: "won" | "lost") =>
    patch.mutate({ id: o.id, status }, { onSuccess: () => toast(`${o.contactName} marked ${status}`, { action: { label: "Undo", onClick: () => patch.mutate({ id: o.id, status: "open" }) } }) })
  const book = async (startsAt: string, kind: "inspection" | "dropoff") => {
    if (!booking) return
    try { const r = await decide.mutateAsync({ kind: "book", contactId: booking.contactId, startsAt, appointmentKind: kind }); toast.success(r.note ?? "Booked"); setBooking(null) } catch { toast.error("Couldn't book — try again") }
  }
  const actions = { onClose: close, onBook: (o: GhlOpportunityRow) => setBooking(o), users }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] sm:w-64">
          <SearchMd className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400" aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search deals" aria-label="Search deals" className={cx(INPUT, "w-full pl-9")} />
        </div>
        <TextSelect ariaLabel="Owner" value={owner} onChange={setOwner} options={[{ value: "", label: "Anyone" }, ...users.map((u) => ({ value: u.id, label: u.name }))]} />
        <p className="text-[13px] text-zinc-500">{opps.length} open · {usdK(opps.reduce((s, o) => s + o.monetaryValue, 0))}</p>
        <div className="ml-auto flex gap-1.5" role="radiogroup" aria-label="View">
          <button type="button" role="radio" aria-checked={view === "board"} onClick={() => setView("board")} className={cx(pill(view === "board"), "h-9 px-3")}>Board</button>
          <button type="button" role="radio" aria-checked={view === "list"} onClick={() => setView("list")} className={cx(pill(view === "list"), "h-9 px-3")}>List</button>
        </div>
      </div>

      {isLoading && all.length === 0 ? <Empty>Loading the board…</Empty> : view === "board" ? (
        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={onDragEnd}>
          <div className="mt-4 flex gap-3 overflow-x-auto pb-3">
            {stages.map((s) => <Column key={s.id} stage={s} items={opps.filter((o) => o.pipelineStageId === s.id)} {...actions} />)}
          </div>
        </DndContext>
      ) : (
        <ListView opps={opps} stages={stages} onStage={(o, stage) => patch.mutate({ id: o.id, pipelineStageId: stage })} {...actions} />
      )}

      <BookDialog open={!!booking} teamId={teamId} contactName={booking?.contactName ?? ""} onClose={() => setBooking(null)} onBook={book} />
    </div>
  )
}

type Actions = { onClose: (o: GhlOpportunityRow, status: "won" | "lost") => void; onBook: (o: GhlOpportunityRow) => void; users: ReturnType<typeof useGhlUsers>["data"] }

function Column({ stage, items, ...a }: { stage: GhlStage; items: GhlOpportunityRow[] } & Actions) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id })
  const total = items.reduce((s, o) => s + o.monetaryValue, 0)
  return (
    <section ref={setNodeRef} aria-label={stage.name} className={cx("flex w-[280px] shrink-0 flex-col rounded-xl border bg-zinc-50/70 transition-colors", isOver ? "border-zinc-900 bg-zinc-100" : "border-zinc-200")}>
      <header className="px-3 pb-2 pt-3">
        <h3 className="text-[13px] font-semibold text-zinc-900">{stage.name}</h3>
        <p className="text-[12px] text-zinc-500">{items.length} {items.length === 1 ? "deal" : "deals"} · <span className="font-semibold text-emerald-700">{usdK(total)}</span></p>
      </header>
      <ul className="flex min-h-[120px] flex-col gap-2 px-2 pb-2">{items.map((o) => <Card key={o.id} o={o} {...a} />)}</ul>
    </section>
  )
}

function Card({ o, onClose, onBook, users }: { o: GhlOpportunityRow } & Actions) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: o.id })
  return (
    <li ref={setNodeRef} style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined} className={cx("rounded-lg border border-zinc-200 bg-white p-3 shadow-sm", isDragging && "z-10 shadow-lg ring-2 ring-zinc-900/20")}>
      <div {...attributes} {...listeners} className="cursor-grab touch-none active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30" aria-label={`Drag ${o.contactName}`}>
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 truncate text-[14px] font-semibold text-zinc-900">{o.contactName}</p>
          <p className="shrink-0 text-[14px] font-semibold tabular-nums text-zinc-900">{usd(o.monetaryValue)}</p>
        </div>
        <p className="truncate text-[12.5px] text-zinc-500">{o.vehicle ?? o.name}</p>
        <p className="mt-1 text-[11.5px] text-zinc-400">{userName(users, o.assignedTo)} · {relTime(o.lastStageChangeAt)}</p>
      </div>
      <div className="mt-2 flex items-center gap-0.5 border-t border-zinc-100 pt-2">
        <Link to={`/marketing/contacts/${o.contactId}?compose=1`} className="h-7 rounded-md px-1.5 text-[12px] font-semibold text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900">Text</Link>
        <Link to={`/marketing/contacts/${o.contactId}`} className="h-7 rounded-md px-1.5 text-[12px] font-semibold text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"><Phone01 className="mr-0.5 inline size-3" aria-hidden />Open</Link>
        <button type="button" onClick={() => onBook(o)} className="h-7 rounded-md px-1.5 text-[12px] font-semibold text-zinc-900 hover:bg-zinc-100">Book</button>
        <button type="button" onClick={() => onClose(o, "won")} className="ml-auto h-7 rounded-md px-1.5 text-[12px] font-semibold text-emerald-700 hover:bg-emerald-50">Won</button>
        <button type="button" onClick={() => onClose(o, "lost")} className="h-7 rounded-md px-1.5 text-[12px] font-semibold text-zinc-500 hover:bg-zinc-100">Lost</button>
      </div>
    </li>
  )
}

function ListView({ opps, stages, onStage, onClose, onBook, users }: { opps: GhlOpportunityRow[]; stages: GhlStage[]; onStage: (o: GhlOpportunityRow, stage: string) => void } & Actions) {
  const sorted = [...opps].sort((a, b) => stages.findIndex((s) => s.id === a.pipelineStageId) - stages.findIndex((s) => s.id === b.pipelineStageId) || b.monetaryValue - a.monetaryValue)
  return (
    <div className="mt-4 overflow-x-auto rounded-xl border border-zinc-200 bg-white">
      <table className="w-full min-w-[900px] table-fixed">
        <colgroup><col /><col className="w-[170px]" /><col className="w-[190px]" /><col className="w-[100px]" /><col className="w-[140px]" /><col className="w-[100px]" /><col className="w-[220px]" /></colgroup>
        <thead><tr className="border-b border-zinc-100">{["Contact", "Vehicle", "Stage", "Value", "Owner", "Moved", ""].map((h) => <th key={h} scope="col" className="px-3 py-2.5 text-left text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500">{h}</th>)}</tr></thead>
        <tbody>
          {sorted.map((o) => (
            <tr key={o.id} className="border-t border-zinc-100">
              <td className="px-3 py-2"><Link to={`/marketing/contacts/${o.contactId}`} className="text-[14px] font-semibold text-zinc-900 hover:underline">{o.contactName}</Link></td>
              <td className="truncate px-3 py-2 text-[13px] text-zinc-700">{o.vehicle ?? "—"}</td>
              <td className="px-2 py-2"><TextSelect ariaLabel={`${o.contactName}: stage`} value={o.pipelineStageId} onChange={(v) => onStage(o, v)} options={stages.map((s) => ({ value: s.id, label: s.name }))} /></td>
              <td className="px-3 py-2 text-[14px] font-semibold tabular-nums text-zinc-900">{usd(o.monetaryValue)}</td>
              <td className="truncate px-3 py-2 text-[13px] text-zinc-700">{userName(users, o.assignedTo)}</td>
              <td className="px-3 py-2 text-[12.5px] text-zinc-500">{relTime(o.lastStageChangeAt)}</td>
              <td className="px-2 py-2 text-right">
                <button type="button" onClick={() => onBook(o)} className="h-8 rounded-md px-2 text-[12.5px] font-semibold text-zinc-900 hover:bg-zinc-100">Book</button>
                <button type="button" onClick={() => onClose(o, "won")} className="h-8 rounded-md px-2 text-[12.5px] font-semibold text-emerald-700 hover:bg-emerald-50">Won</button>
                <button type="button" onClick={() => onClose(o, "lost")} className="h-8 rounded-md px-2 text-[12.5px] font-semibold text-zinc-500 hover:bg-zinc-100">Lost</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
